import { useState } from 'react';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Snackbar from '@material-ui/core/Snackbar';
import DeleteIcon from '@material-ui/icons/Delete';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import { Role } from 'types/schema.type';
import { useGetSharedTokens } from './actions/useGetSharedTokens';
import { useCreateSharedToken } from './actions/useCreateSharedToken';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  paper: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  linkContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    maxWidth: '100%',
  },
  expiredLink: {
    color: `${theme.palette.error.main} !important`,
  },
  link: {
    color: theme.palette.text.primary,
    marginRight: theme.spacing(1),
    // The token makes the URL ~80 chars: keep it on one line and trim the
    // middle of the screen-width, the copy button carries the full value.
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);

      return true;
    }
  } catch {
    // fall through to the legacy path (older Safari / non-secure contexts)
  }

  const textarea = document.createElement('textarea');

  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();

  let copied = false;

  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }

  document.body.removeChild(textarea);

  return copied;
}

export function SharedLinks({ user }: { user: any }) {
  const classes = useStyles();
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const { tokens, loading } = useGetSharedTokens();
  const { createSharedToken, deleteSharedToken } = useCreateSharedToken();

  if (user.role === Role.Clinic) {
    return null;
  }

  if (loading || !tokens) {
    return <div>Loading...</div>;
  }

  const baseURL = window.location.origin;

  const isExpired = (expiresAt: Date): boolean => new Date(expiresAt) < new Date();

  // The backend refuses share tokens for any elevated role (#490): a link
  // generated by an ADMIN/DOCTOR/ANNOTATOR account can never be opened.
  // Legacy accounts may carry no role at all — those are patients.
  const isPatient = !user.role || user.role === Role.User;
  const shouldBeDisabled = !isPatient || loading || tokens.length > 0;

  const onCopy = async (link: string) => {
    const copied = await copyToClipboard(link);

    setCopyMessage(copied ? 'Link copied to clipboard' : 'Could not copy — select the link and copy it manually');
  };

  return (
    <div className={classes.root}>
      <div className={classes.paper}>
        <Typography>Shared links:</Typography>

        {!isPatient && (
          <Typography color="textSecondary">
            Shared links are available for patient accounts only.
          </Typography>
        )}

        {isPatient && !tokens.length && <Typography>No shared links yet</Typography>}

        {tokens.map((token) => {
          const expired = isExpired(new Date(token.expiresAt));
          const link = `${baseURL}?sharedToken=${token.token}`;

          return (
            <div key={token.id} className={classes.linkContainer}>
              <Tooltip
                placement="top"
                title={expired ? 'This link has expired' : link}
                arrow
              >
                <Typography className={`${classes.link} ${expired ? classes.expiredLink : ''}`}>
                  {link}
                </Typography>
              </Tooltip>
              <Tooltip placement="top" title="Copy link" arrow>
                <IconButton aria-label="copy link" size="small" onClick={() => onCopy(link)}>
                  <FileCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip placement="top" title="Delete link" arrow>
                <IconButton
                  aria-label="delete"
                  size="small"
                  onClick={() => deleteSharedToken({ variables: { tokenId: token.id } })}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          );
        })}

        <Button
          color="primary"
          disabled={shouldBeDisabled}
          variant="contained"
          fullWidth
          onClick={() => {
            createSharedToken();
          }}
        >
          Generate a shared link
        </Button>
      </div>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={2500}
        message={copyMessage}
        open={Boolean(copyMessage)}
        onClose={() => setCopyMessage(null)}
      />
    </div>
  );
}
